import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./ReferralManagementPage.css";

const EXPORT_BATCH = 500;

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const asNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const userDisplayName = (row) => {
  const parts = [row?.firstName, row?.lastName].filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
};

const referrerToExport = (row) => ({
  Email: row.emailId ?? "",
  UUID: row.uuid ?? "",
  "Referral Code": row.referral_code ?? "",
  "Total Refer": row.total_refer ?? 0,
  "Direct Downline": row.direct_downline_count ?? 0,
  "Total Earnings": row.total_referral_earnings ?? 0,
  "Wallet Balance": row.referral_wallet_balance ?? 0,
  "Created At": row.createdAt ?? "",
});

const rewardLogToExport = (row) => ({
  Receiver: row?.receiver?.emailId ?? "",
  "From User": row?.from_user?.emailId ?? "",
  Level: row.level ?? "",
  Type: row.type ?? "",
  Amount: row.amount ?? 0,
  "Short Name": row.shortName ?? "",
  "Created At": row.createdAt ?? "",
});

const signupTaskToExport = (row) => {
  const tasks = Array.isArray(row.tasks)
    ? [...row.tasks].sort((a, b) => asNum(a?.task_id) - asNum(b?.task_id))
    : [];
  const base = {
    Name: userDisplayName(row) === "—" ? "" : userDisplayName(row),
    Email: row.emailId ?? "",
    Mobile: row.mobileNumber ?? "",
    UUID: row.uuid ?? "",
    Referral: row.referral_code ?? "",
    Registered: row.registeredBy ?? "",
    KYC: row.kycVerified ?? "",
    "Progress Completed": row.completed_count ?? 0,
    "Progress Total": row.total_tasks ?? "",
    "Social Done": row.social_tasks_completed_flag ? "Yes" : "No",
    Reward: row.reward_claimed ? "Claimed" : "No",
  };
  tasks.forEach((t, i) => {
    base[`Task ${i + 1} (${t.task_id ?? i + 1})`] = t.completed ? "Done" : "Pending";
    base[`Task ${i + 1} Label`] = t.label ?? "";
  });
  return base;
};

const TABS = [
  { key: "referrers", label: "Referrers", icon: "fa-users" },
  { key: "rewardLogs", label: "Reward Logs", icon: "fa-file-invoice-dollar" },
  { key: "airdropTask", label: "Airdrop task", icon: "fa-tasks" },
];

const PAGE_LIMIT_OPTIONS = [20, 50, 100, 500, 1000];

const ReferralSearchBar = ({ value, onChange, onSubmit, placeholder, ariaLabel }) => (
  <div className="referral-airdrop-toolbar">
    <form onSubmit={onSubmit}>
      <input
        type="search"
        className="referral-airdrop-search-input"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        aria-label={ariaLabel}
      />
      <button type="submit" className="referral-airdrop-search-btn">
        Search
      </button>
    </form>
  </div>
);

const TabPaginationBar = ({ pagination, loading, onPageChange, onLimitChange }) => {
  const page = pagination.page || 1;
  const limit = pagination.limit || 20;
  const total = pagination.total ?? 0;
  const totalPages = pagination.total_pages || 1;
  const pageStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const pageEnd = total === 0 ? 0 : Math.min(page * limit, total);

  return (
    <div className="referral-pagination">
      <label className="referral-page-limit">
        <span>Rows per page</span>
        <select
          value={limit}
          onChange={(e) => onLimitChange(Number(e.target.value) || 20)}
          disabled={loading}
        >
          {PAGE_LIMIT_OPTIONS.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>
      <span className="referral-pagination__info">
        {total > 0 ? (
          <>
            Showing {pageStart}–{pageEnd} of {total} · Page {page} of {totalPages}
          </>
        ) : (
          <>Page {page} of {totalPages}</>
        )}
      </span>
      <div className="referral-pagination__nav">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1 || loading}
        >
          Prev
        </button>
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages || loading}
        >
          Next
        </button>
      </div>
    </div>
  );
};

const ReferralManagementPage = () => {
  const [activeTab, setActiveTab] = useState("referrers");

  const [referrers, setReferrers] = useState([]);
  const [referrersLoading, setReferrersLoading] = useState(false);
  const [referrersError, setReferrersError] = useState("");
  const [referrersPagination, setReferrersPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 1 });
  const [referrersSearch, setReferrersSearch] = useState("");
  const [referrersSearchDraft, setReferrersSearchDraft] = useState("");

  const [rewardLogs, setRewardLogs] = useState([]);
  const [rewardLogsLoading, setRewardLogsLoading] = useState(false);
  const [rewardLogsError, setRewardLogsError] = useState("");
  const [rewardLogsPagination, setRewardLogsPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 1 });
  const [rewardLogsSearch, setRewardLogsSearch] = useState("");
  const [rewardLogsSearchDraft, setRewardLogsSearchDraft] = useState("");

  const [selectedUserId, setSelectedUserId] = useState("");
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [userDetailsError, setUserDetailsError] = useState("");
  const [userSummary, setUserSummary] = useState(null);
  const [userInvitees, setUserInvitees] = useState([]);
  const [userEarningLogs, setUserEarningLogs] = useState([]);

  const [signupTaskRows, setSignupTaskRows] = useState([]);
  const [signupTaskMeta, setSignupTaskMeta] = useState(null);
  const [signupTaskLoading, setSignupTaskLoading] = useState(false);
  const [signupTaskError, setSignupTaskError] = useState("");
  const [signupTaskPagination, setSignupTaskPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 1 });
  const [signupTaskSearch, setSignupTaskSearch] = useState("");
  const [signupTaskSearchDraft, setSignupTaskSearchDraft] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [exportToast, setExportToast] = useState(null);

  const fetchReferrers = async (page = 1, limit = referrersPagination.limit, searchOverride) => {
    const search = typeof searchOverride === "string" ? searchOverride : referrersSearch;
    setReferrersLoading(true);
    setReferrersError("");
    try {
      const res = await AdminService.getReferralReferrers({
        page,
        limit,
        ...(search.trim() && { search: search.trim() }),
      });
      if (res?.success) {
        setReferrers(Array.isArray(res?.data) ? res.data : []);
        setReferrersPagination((prev) => ({
          ...prev,
          ...(res?.pagination || {}),
          page: res?.pagination?.page || page,
          limit: res?.pagination?.limit ?? limit,
        }));
      } else {
        setReferrersError(res?.message || "Failed to fetch referrers.");
      }
    } catch (err) {
      setReferrersError(err?.response?.data?.message || err?.message || "Failed to fetch referrers.");
    } finally {
      setReferrersLoading(false);
    }
  };

  const fetchRewardLogs = async (page = 1, limit = rewardLogsPagination.limit, searchOverride) => {
    const search = typeof searchOverride === "string" ? searchOverride : rewardLogsSearch;
    setRewardLogsLoading(true);
    setRewardLogsError("");
    try {
      const res = await AdminService.getReferralRewardLogs({
        page,
        limit,
        ...(search.trim() && { search: search.trim() }),
      });
      if (res?.success) {
        setRewardLogs(Array.isArray(res?.data) ? res.data : []);
        setRewardLogsPagination((prev) => ({
          ...prev,
          ...(res?.pagination || {}),
          page: res?.pagination?.page || page,
          limit: res?.pagination?.limit ?? limit,
        }));
      } else {
        setRewardLogsError(res?.message || "Failed to fetch reward logs.");
      }
    } catch (err) {
      setRewardLogsError(err?.response?.data?.message || err?.message || "Failed to fetch reward logs.");
    } finally {
      setRewardLogsLoading(false);
    }
  };

  const fetchSignupTaskProgress = async (page = 1, searchOverride, limitOverride) => {
    const search = typeof searchOverride === "string" ? searchOverride : signupTaskSearch;
    const limit = limitOverride ?? signupTaskPagination.limit;
    setSignupTaskLoading(true);
    setSignupTaskError("");
    try {
      const res = await AdminService.getReferralSignupTaskProgress({
        page,
        limit,
        ...(search.trim() && { search: search.trim() }),
      });
      if (res?.success) {
        setSignupTaskRows(Array.isArray(res?.data) ? res.data : []);
        setSignupTaskMeta(res?.meta ?? null);
        setSignupTaskPagination((prev) => ({
          ...prev,
          ...(res?.pagination || {}),
          page: res?.pagination?.page ?? page,
          limit: res?.pagination?.limit ?? limit,
        }));
      } else {
        setSignupTaskError(res?.message || "Failed to load signup task progress.");
      }
    } catch (err) {
      setSignupTaskError(err?.response?.data?.message || err?.message || "Failed to load signup task progress.");
    } finally {
      setSignupTaskLoading(false);
    }
  };

  const handleReferrersSearchSubmit = (e) => {
    e.preventDefault();
    const q = referrersSearchDraft.trim();
    setReferrersSearch(q);
    fetchReferrers(1, referrersPagination.limit, q);
  };

  const handleRewardLogsSearchSubmit = (e) => {
    e.preventDefault();
    const q = rewardLogsSearchDraft.trim();
    setRewardLogsSearch(q);
    fetchRewardLogs(1, rewardLogsPagination.limit, q);
  };

  const handleSignupTaskSearchSubmit = (e) => {
    e.preventDefault();
    const q = signupTaskSearchDraft.trim();
    setSignupTaskSearch(q);
    fetchSignupTaskProgress(1, q);
  };

  const fetchUserDetails = async (userId) => {
    if (!userId) return;
    setSelectedUserId(userId);
    setUserDetailsLoading(true);
    setUserDetailsError("");
    setUserSummary(null);
    setUserInvitees([]);
    setUserEarningLogs([]);
    try {
      const [summaryRes, inviteesRes, logsRes] = await Promise.all([
        AdminService.getReferralUserSummary(userId),
        AdminService.getReferralUserInvitees(userId, { page: 1, limit: 20 }),
        AdminService.getReferralUserEarningLogs(userId, { page: 1, limit: 30 }),
      ]);

      if (!summaryRes?.success) {
        setUserDetailsError(summaryRes?.message || "Failed to fetch user summary.");
        return;
      }

      setUserSummary(summaryRes?.data || null);
      setUserInvitees(inviteesRes?.success && Array.isArray(inviteesRes?.data) ? inviteesRes.data : []);
      setUserEarningLogs(logsRes?.success && Array.isArray(logsRes?.data) ? logsRes.data : []);
    } catch (err) {
      setUserDetailsError(err?.response?.data?.message || err?.message || "Failed to fetch user referral details.");
    } finally {
      setUserDetailsLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrers(1);
    fetchRewardLogs(1);
  }, []);

  useEffect(() => {
    if (activeTab !== "airdropTask") return;
    fetchSignupTaskProgress(signupTaskPagination.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load when tab activates; paging uses direct fetch calls
  }, [activeTab]);

  const referrerStats = useMemo(() => {
    return {
      totalReferrers: referrersPagination.total || referrers.length,
      totalWalletBalance: referrers.reduce((sum, x) => sum + asNum(x?.referral_wallet_balance), 0),
      totalReferralEarnings: referrers.reduce((sum, x) => sum + asNum(x?.total_referral_earnings), 0),
    };
  }, [referrers, referrersPagination.total]);

  const fetchAllReferrers = async () => {
    let currentPage = 1;
    let lastPage = 1;
    const all = [];
    do {
      const res = await AdminService.getReferralReferrers({
        page: currentPage,
        limit: EXPORT_BATCH,
        ...(referrersSearch.trim() && { search: referrersSearch.trim() }),
      });
      if (res?.success === false && !Array.isArray(res?.data)) {
        throw new Error(res?.message || "Failed to fetch referrers for export.");
      }
      const list = Array.isArray(res?.data) ? res.data : [];
      all.push(...list);
      lastPage = res?.pagination?.total_pages || 1;
      currentPage += 1;
    } while (currentPage <= lastPage);
    return all;
  };

  const fetchAllRewardLogs = async () => {
    let currentPage = 1;
    let lastPage = 1;
    const all = [];
    do {
      const res = await AdminService.getReferralRewardLogs({
        page: currentPage,
        limit: EXPORT_BATCH,
        ...(rewardLogsSearch.trim() && { search: rewardLogsSearch.trim() }),
      });
      if (res?.success === false && !Array.isArray(res?.data)) {
        throw new Error(res?.message || "Failed to fetch reward logs for export.");
      }
      const list = Array.isArray(res?.data) ? res.data : [];
      all.push(...list);
      lastPage = res?.pagination?.total_pages || 1;
      currentPage += 1;
    } while (currentPage <= lastPage);
    return all;
  };

  const fetchAllSignupTasks = async () => {
    let currentPage = 1;
    let lastPage = 1;
    const all = [];
    do {
      const res = await AdminService.getReferralSignupTaskProgress({
        page: currentPage,
        limit: EXPORT_BATCH,
        ...(signupTaskSearch.trim() && { search: signupTaskSearch.trim() }),
      });
      if (res?.success === false && !Array.isArray(res?.data)) {
        throw new Error(res?.message || "Failed to fetch signup tasks for export.");
      }
      const list = Array.isArray(res?.data) ? res.data : [];
      all.push(...list);
      lastPage = res?.pagination?.total_pages || 1;
      currentPage += 1;
    } while (currentPage <= lastPage);
    return all;
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    setExportToast(null);
    try {
      let allRows = [];
      let sheetTitle = "";
      let filePrefix = "";

      if (activeTab === "referrers") {
        allRows = await fetchAllReferrers();
        sheetTitle = "Referrers";
        filePrefix = "referral_referrers";
      } else if (activeTab === "rewardLogs") {
        allRows = await fetchAllRewardLogs();
        sheetTitle = "Reward Logs";
        filePrefix = "referral_reward_logs";
      } else {
        allRows = await fetchAllSignupTasks();
        sheetTitle = "Airdrop Tasks";
        filePrefix = "referral_airdrop_tasks";
      }

      if (!allRows.length) {
        setExportToast({ type: "error", message: "No data to export for the current tab." });
        setTimeout(() => setExportToast(null), 3000);
        return;
      }

      const exportData =
        activeTab === "referrers"
          ? allRows.map(referrerToExport)
          : activeTab === "rewardLogs"
            ? allRows.map(rewardLogToExport)
            : allRows.map(signupTaskToExport);

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetTitle);
      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `${filePrefix}_${dateStamp}.xlsx`);

      setExportToast({
        type: "success",
        message: `Exported ${allRows.length} row(s) to Excel.`,
      });
      setTimeout(() => setExportToast(null), 4000);
    } catch (err) {
      console.error(err);
      setExportToast({
        type: "error",
        message: err?.message || "Failed to export Excel file.",
      });
      setTimeout(() => setExportToast(null), 4000);
    } finally {
      setExportLoading(false);
    }
  };

  const activeTabLabel = TABS.find((t) => t.key === activeTab)?.label || "Data";

  return (
    <main className="dashboard-main dashboard-subpage referral-management-page">
      <section className="dashboard-hero referral-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Referral Management</span>
          </p>
          <h1 className="dashboard-hero__title">Referral Management</h1>
          <p className="dashboard-hero__subtitle">Track referrers, reward logs, and user referral trees</p>
        </div>
        <div className="referral-hero__icon">
          <i className="fas fa-network-wired" />
        </div>
      </section>

      <div className="dashboard-body">
        <div className="referral-stats">
          <div className="referral-stat-card">
            <span className="referral-stat-card__value">{referrerStats.totalReferrers}</span>
            <span className="referral-stat-card__label">Total Referrers</span>
          </div>
          <div className="referral-stat-card">
            <span className="referral-stat-card__value">{referrerStats.totalReferralEarnings}</span>
            <span className="referral-stat-card__label">Referral Earnings (current page)</span>
          </div>
          <div className="referral-stat-card">
            <span className="referral-stat-card__value">{referrerStats.totalWalletBalance}</span>
            <span className="referral-stat-card__label">Wallet Balance (current page)</span>
          </div>
        </div>

        <div className="referral-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`referral-tab ${activeTab === tab.key ? "referral-tab--active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <i className={`fas ${tab.icon}`} /> {tab.label}
            </button>
          ))}
          <button
            type="button"
            className="referral-export-btn"
            onClick={handleExportExcel}
            disabled={exportLoading}
            title={`Download ${activeTabLabel} as Excel`}
          >
            <i className={`fas ${exportLoading ? "fa-spinner fa-spin" : "fa-file-excel"}`} />
            {exportLoading ? "Exporting…" : "Download Excel"}
          </button>
        </div>

        {exportToast && (
          <p className={`referral-export-toast referral-export-toast--${exportToast.type}`} role="status">
            {exportToast.message}
          </p>
        )}

        {activeTab === "referrers" && (
          <section className="referral-section">
            <h2 className="referral-section__title"><i className="fas fa-users" /> Referrers</h2>
            <ReferralSearchBar
              value={referrersSearchDraft}
              onChange={(e) => setReferrersSearchDraft(e.target.value)}
              onSubmit={handleReferrersSearchSubmit}
              placeholder="Search email, UUID, referral code..."
              ariaLabel="Search referrers"
            />
            {referrersLoading ? (
              <div className="referral-empty"><i className="fas fa-spinner fa-spin" /><p>Loading referrers...</p></div>
            ) : referrersError ? (
              <div className="referral-empty"><i className="fas fa-exclamation-circle" /><p>{referrersError}</p></div>
            ) : referrers.length === 0 ? (
              <div className="referral-empty"><i className="fas fa-users" /><p>{referrersSearch ? "No referrers match this search" : "No referrers found"}</p></div>
            ) : (
              <>
                <div className="referral-table-wrap">
                  <table className="referral-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>UUID</th>
                        <th>Referral Code</th>
                        <th>Total Refer</th>
                        <th>Direct Downline</th>
                        <th>Total Earnings</th>
                        <th>Wallet Balance</th>
                        <th>Created At</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referrers.map((row) => (
                        <tr key={row._id}>
                          <td>{row.emailId || "—"}</td>
                          <td>{row.uuid || "—"}</td>
                          <td>{row.referral_code || "—"}</td>
                          <td>{row.total_refer ?? 0}</td>
                          <td>{row.direct_downline_count ?? 0}</td>
                          <td>{row.total_referral_earnings ?? 0}</td>
                          <td>{row.referral_wallet_balance ?? 0}</td>
                          <td>{formatDate(row.createdAt)}</td>
                          <td>
                            <button type="button" className="referral-btn-view" onClick={() => fetchUserDetails(row._id)}>
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <TabPaginationBar
                  pagination={referrersPagination}
                  loading={referrersLoading}
                  onPageChange={(p) => fetchReferrers(p, referrersPagination.limit, referrersSearch)}
                  onLimitChange={(limit) => {
                    setReferrersPagination((prev) => ({ ...prev, limit, page: 1 }));
                    fetchReferrers(1, limit, referrersSearch);
                  }}
                />
              </>
            )}
          </section>
        )}

        {activeTab === "rewardLogs" && (
          <section className="referral-section">
            <h2 className="referral-section__title"><i className="fas fa-file-invoice-dollar" /> Reward Logs</h2>
            <ReferralSearchBar
              value={rewardLogsSearchDraft}
              onChange={(e) => setRewardLogsSearchDraft(e.target.value)}
              onSubmit={handleRewardLogsSearchSubmit}
              placeholder="Search receiver, from user, type, short name..."
              ariaLabel="Search reward logs"
            />
            {rewardLogsLoading ? (
              <div className="referral-empty"><i className="fas fa-spinner fa-spin" /><p>Loading reward logs...</p></div>
            ) : rewardLogsError ? (
              <div className="referral-empty"><i className="fas fa-exclamation-circle" /><p>{rewardLogsError}</p></div>
            ) : rewardLogs.length === 0 ? (
              <div className="referral-empty"><i className="fas fa-file-invoice-dollar" /><p>{rewardLogsSearch ? "No reward logs match this search" : "No reward logs found"}</p></div>
            ) : (
              <>
                <div className="referral-table-wrap">
                  <table className="referral-table">
                    <thead>
                      <tr>
                        <th>Receiver</th>
                        <th>From User</th>
                        <th>Level</th>
                        <th>Type</th>
                        <th>Amount</th>
                        <th>Short Name</th>
                        <th>Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rewardLogs.map((row) => (
                        <tr key={row._id}>
                          <td>{row?.receiver?.emailId || "—"}</td>
                          <td>{row?.from_user?.emailId || "—"}</td>
                          <td>{row.level ?? "—"}</td>
                          <td>{row.type || "—"}</td>
                          <td>{row.amount ?? 0}</td>
                          <td>{row.shortName || "—"}</td>
                          <td>{formatDate(row.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <TabPaginationBar
                  pagination={rewardLogsPagination}
                  loading={rewardLogsLoading}
                  onPageChange={(p) => fetchRewardLogs(p, rewardLogsPagination.limit, rewardLogsSearch)}
                  onLimitChange={(limit) => {
                    setRewardLogsPagination((prev) => ({ ...prev, limit, page: 1 }));
                    fetchRewardLogs(1, limit, rewardLogsSearch);
                  }}
                />
              </>
            )}
          </section>
        )}

        {activeTab === "airdropTask" && (
          <section className="referral-section">
            <h2 className="referral-section__title"><i className="fas fa-tasks" /> Airdrop task</h2>

            {signupTaskMeta ? (
              <div className="referral-airdrop-meta">
                <strong>Signup referral tasks:</strong> link / social IDs {String(signupTaskMeta.link_task_ids ?? "—")}
                {" · "}
                <strong>Mobile task:</strong> {signupTaskMeta.mobile_task_id ?? "—"}
                {" · "}
                <strong>Prerequisite tasks:</strong> {signupTaskMeta.total_prerequisite_tasks ?? "—"}
                {signupTaskPagination.total != null ? (
                  <>
                    {" · "}
                    <strong>Total users (filter):</strong> {signupTaskPagination.total}
                  </>
                ) : null}
              </div>
            ) : null}

            <ReferralSearchBar
              value={signupTaskSearchDraft}
              onChange={(e) => setSignupTaskSearchDraft(e.target.value)}
              onSubmit={handleSignupTaskSearchSubmit}
              placeholder="Search email, UUID, referral, mobile..."
              ariaLabel="Search signup task users"
            />

            {signupTaskLoading ? (
              <div className="referral-empty"><i className="fas fa-spinner fa-spin" /><p>Loading signup task progress…</p></div>
            ) : signupTaskError ? (
              <div className="referral-empty"><i className="fas fa-exclamation-circle" /><p>{signupTaskError}</p></div>
            ) : signupTaskRows.length === 0 ? (
              <div className="referral-empty"><i className="fas fa-tasks" /><p>No users match this filter</p></div>
            ) : (
              <>
                <div className="referral-table-wrap">
                  <table className="referral-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Mobile</th>
                        <th>UUID</th>
                        <th>Referral</th>
                        <th>Reg.</th>
                        <th>KYC</th>
                        <th>Progress</th>
                        <th>Social done</th>
                        <th>Reward</th>
                        <th title="Tasks 1–5 social links; 6 mobile">Tasks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {signupTaskRows.map((row) => (
                        <tr key={row._id}>
                          <td>{userDisplayName(row)}</td>
                          <td>{row.emailId || "—"}</td>
                          <td>{row.mobileNumber || "—"}</td>
                          <td>{row.uuid ?? "—"}</td>
                          <td>{row.referral_code || "—"}</td>
                          <td>{row.registeredBy || "—"}</td>
                          <td>{row.kycVerified ?? "—"}</td>
                          <td>
                            {row.completed_count ?? 0}/{row.total_tasks ?? "—"}
                          </td>
                          <td>{row.social_tasks_completed_flag ? "Yes" : "No"}</td>
                          <td>{row.reward_claimed ? "Claimed" : "No"}</td>
                          <td>
                            <span className="referral-task-cells">
                              {(Array.isArray(row.tasks) ? row.tasks : [])
                                .slice()
                                .sort((a, b) => asNum(a?.task_id) - asNum(b?.task_id))
                                .map((t) => (
                                  <span
                                    key={`${row._id}-t-${t.task_id}`}
                                    className={`referral-task-pill ${t.completed ? "referral-task-pill--done" : "referral-task-pill--todo"}`}
                                    title={[t.label, t.kind ? `(${t.kind})` : "", t.pending_sync_only ? "pending sync" : ""].filter(Boolean).join(" ")}
                                  >
                                    {t.completed ? "✓" : "—"}
                                  </span>
                                ))}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <TabPaginationBar
                  pagination={signupTaskPagination}
                  loading={signupTaskLoading}
                  onPageChange={(p) => fetchSignupTaskProgress(p, signupTaskSearch, signupTaskPagination.limit)}
                  onLimitChange={(limit) => {
                    setSignupTaskPagination((prev) => ({ ...prev, limit, page: 1 }));
                    fetchSignupTaskProgress(1, signupTaskSearch, limit);
                  }}
                />
              </>
            )}
          </section>
        )}

        <section className="referral-section referral-user-details" hidden={activeTab !== "referrers"} aria-hidden={activeTab !== "referrers" ? "true" : undefined}>
          <h2 className="referral-section__title"><i className="fas fa-user-circle" /> User Referral Details</h2>
          {!selectedUserId ? (
            <div className="referral-empty"><i className="fas fa-info-circle" /><p>Select a referrer and click View Details</p></div>
          ) : userDetailsLoading ? (
            <div className="referral-empty"><i className="fas fa-spinner fa-spin" /><p>Loading user details...</p></div>
          ) : userDetailsError ? (
            <div className="referral-empty"><i className="fas fa-exclamation-circle" /><p>{userDetailsError}</p></div>
          ) : (
            <>
              <div className="referral-user-summary-grid">
                <div><strong>Email:</strong> {userSummary?.user?.emailId || "—"}</div>
                <div><strong>UUID:</strong> {userSummary?.user?.uuid || "—"}</div>
                <div><strong>Referral Code:</strong> {userSummary?.user?.referral_code || "—"}</div>
                <div><strong>Total Refer:</strong> {userSummary?.user?.total_refer ?? 0}</div>
                <div><strong>Direct Downline:</strong> {userSummary?.direct_downline_count ?? 0}</div>
                <div><strong>Total Earned From Logs:</strong> {userSummary?.total_earned_from_logs ?? 0}</div>
              </div>

              <div className="referral-subsection">
                <h3>Invitees</h3>
                {userInvitees.length === 0 ? (
                  <p className="referral-muted">No direct invitees.</p>
                ) : (
                  <div className="referral-table-wrap">
                    <table className="referral-table">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>UUID</th>
                          <th>Referral Code</th>
                          <th>KYC</th>
                          <th>Reward Claimed</th>
                          <th>Created At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userInvitees.map((x) => (
                          <tr key={x._id}>
                            <td>{x.emailId || "—"}</td>
                            <td>{x.uuid || "—"}</td>
                            <td>{x.referral_code || "—"}</td>
                            <td>{x.kycVerified ?? "—"}</td>
                            <td>{x.rewardClaimed ? "Yes" : "No"}</td>
                            <td>{formatDate(x.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="referral-subsection">
                <h3>Earning Logs</h3>
                {userEarningLogs.length === 0 ? (
                  <p className="referral-muted">No earning logs.</p>
                ) : (
                  <div className="referral-table-wrap">
                    <table className="referral-table">
                      <thead>
                        <tr>
                          <th>From User</th>
                          <th>Level</th>
                          <th>Type</th>
                          <th>Amount</th>
                          <th>Short Name</th>
                          <th>Created At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userEarningLogs.map((x, idx) => (
                          <tr key={`${x.fromUserId || "row"}-${x.createdAt || idx}`}>
                            <td>{x?.from_user?.emailId || "—"}</td>
                            <td>{x.level ?? "—"}</td>
                            <td>{x.type || "—"}</td>
                            <td>{x.amount ?? 0}</td>
                            <td>{x.shortName || "—"}</td>
                            <td>{formatDate(x.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </main>
  );
};

export default ReferralManagementPage;
